/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { AssessmentsMetadata } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SecurityCenter } from "../securityCenter";
import {
  SecurityAssessmentMetadataResponse,
  AssessmentsMetadataListNextOptionalParams,
  AssessmentsMetadataListOptionalParams,
  AssessmentsMetadataListBySubscriptionNextOptionalParams,
  AssessmentsMetadataListBySubscriptionOptionalParams,
  AssessmentsMetadataListResponse,
  AssessmentsMetadataGetOptionalParams,
  AssessmentsMetadataGetResponse,
  AssessmentsMetadataListBySubscriptionResponse,
  AssessmentsMetadataGetInSubscriptionOptionalParams,
  AssessmentsMetadataGetInSubscriptionResponse,
  AssessmentsMetadataCreateInSubscriptionOptionalParams,
  AssessmentsMetadataCreateInSubscriptionResponse,
  AssessmentsMetadataDeleteInSubscriptionOptionalParams,
  AssessmentsMetadataListNextResponse,
  AssessmentsMetadataListBySubscriptionNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing AssessmentsMetadata operations. */
export class AssessmentsMetadataImpl implements AssessmentsMetadata {
  private readonly client: SecurityCenter;

  /**
   * Initialize a new instance of the class AssessmentsMetadata class.
   * @param client Reference to the service client
   */
  constructor(client: SecurityCenter) {
    this.client = client;
  }

  /**
   * Get metadata information on all assessment types
   * @param options The options parameters.
   */
  public list(
    options?: AssessmentsMetadataListOptionalParams
  ): PagedAsyncIterableIterator<SecurityAssessmentMetadataResponse> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listPagingPage(options);
      }
    };
  }

  private async *listPagingPage(
    options?: AssessmentsMetadataListOptionalParams
  ): AsyncIterableIterator<SecurityAssessmentMetadataResponse[]> {
    let result = await this._list(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listPagingAll(
    options?: AssessmentsMetadataListOptionalParams
  ): AsyncIterableIterator<SecurityAssessmentMetadataResponse> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Get metadata information on all assessment types in a specific subscription
   * @param options The options parameters.
   */
  public listBySubscription(
    options?: AssessmentsMetadataListBySubscriptionOptionalParams
  ): PagedAsyncIterableIterator<SecurityAssessmentMetadataResponse> {
    const iter = this.listBySubscriptionPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listBySubscriptionPagingPage(options);
      }
    };
  }

  private async *listBySubscriptionPagingPage(
    options?: AssessmentsMetadataListBySubscriptionOptionalParams
  ): AsyncIterableIterator<SecurityAssessmentMetadataResponse[]> {
    let result = await this._listBySubscription(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listBySubscriptionNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listBySubscriptionPagingAll(
    options?: AssessmentsMetadataListBySubscriptionOptionalParams
  ): AsyncIterableIterator<SecurityAssessmentMetadataResponse> {
    for await (const page of this.listBySubscriptionPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Get metadata information on all assessment types
   * @param options The options parameters.
   */
  private _list(
    options?: AssessmentsMetadataListOptionalParams
  ): Promise<AssessmentsMetadataListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * Get metadata information on an assessment type
   * @param assessmentMetadataName The Assessment Key - Unique key for the assessment type
   * @param options The options parameters.
   */
  get(
    assessmentMetadataName: string,
    options?: AssessmentsMetadataGetOptionalParams
  ): Promise<AssessmentsMetadataGetResponse> {
    return this.client.sendOperationRequest(
      { assessmentMetadataName, options },
      getOperationSpec
    );
  }

  /**
   * Get metadata information on all assessment types in a specific subscription
   * @param options The options parameters.
   */
  private _listBySubscription(
    options?: AssessmentsMetadataListBySubscriptionOptionalParams
  ): Promise<AssessmentsMetadataListBySubscriptionResponse> {
    return this.client.sendOperationRequest(
      { options },
      listBySubscriptionOperationSpec
    );
  }

  /**
   * Get metadata information on an assessment type in a specific subscription
   * @param assessmentMetadataName The Assessment Key - Unique key for the assessment type
   * @param options The options parameters.
   */
  getInSubscription(
    assessmentMetadataName: string,
    options?: AssessmentsMetadataGetInSubscriptionOptionalParams
  ): Promise<AssessmentsMetadataGetInSubscriptionResponse> {
    return this.client.sendOperationRequest(
      { assessmentMetadataName, options },
      getInSubscriptionOperationSpec
    );
  }

  /**
   * Create metadata information on an assessment type in a specific subscription
   * @param assessmentMetadataName The Assessment Key - Unique key for the assessment type
   * @param assessmentMetadata AssessmentMetadata object
   * @param options The options parameters.
   */
  createInSubscription(
    assessmentMetadataName: string,
    assessmentMetadata: SecurityAssessmentMetadataResponse,
    options?: AssessmentsMetadataCreateInSubscriptionOptionalParams
  ): Promise<AssessmentsMetadataCreateInSubscriptionResponse> {
    return this.client.sendOperationRequest(
      { assessmentMetadataName, assessmentMetadata, options },
      createInSubscriptionOperationSpec
    );
  }

  /**
   * Delete metadata information on an assessment type in a specific subscription, will cause the
   * deletion of all the assessments of that type in that subscription
   * @param assessmentMetadataName The Assessment Key - Unique key for the assessment type
   * @param options The options parameters.
   */
  deleteInSubscription(
    assessmentMetadataName: string,
    options?: AssessmentsMetadataDeleteInSubscriptionOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { assessmentMetadataName, options },
      deleteInSubscriptionOperationSpec
    );
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: AssessmentsMetadataListNextOptionalParams
  ): Promise<AssessmentsMetadataListNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listNextOperationSpec
    );
  }

  /**
   * ListBySubscriptionNext
   * @param nextLink The nextLink from the previous successful call to the ListBySubscription method.
   * @param options The options parameters.
   */
  private _listBySubscriptionNext(
    nextLink: string,
    options?: AssessmentsMetadataListBySubscriptionNextOptionalParams
  ): Promise<AssessmentsMetadataListBySubscriptionNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listBySubscriptionNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/providers/Microsoft.Security/assessmentMetadata",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponseList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/providers/Microsoft.Security/assessmentMetadata/{assessmentMetadataName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponse
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [Parameters.$host, Parameters.assessmentMetadataName],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySubscriptionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/assessmentMetadata",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponseList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer
};
const getInSubscriptionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/assessmentMetadata/{assessmentMetadataName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponse
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.assessmentMetadataName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const createInSubscriptionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/assessmentMetadata/{assessmentMetadataName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponse
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  requestBody: Parameters.assessmentMetadata,
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.assessmentMetadataName
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const deleteInSubscriptionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/assessmentMetadata/{assessmentMetadataName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.assessmentMetadataName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponseList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [Parameters.$host, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySubscriptionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SecurityAssessmentMetadataResponseList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion10],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
